import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getCompanies,
  getCompanyDetails,
} from "../../Redux/store/companySlice";
import { showToast } from "../../Utils/toastUtils";
import {
  switchToCompany,
  switchToPersonal,
  getActiveAccountType,
  ACCOUNT_TYPES,
  getCurrentUserInfo,
  saveCompanyTokens,
  hasCompanyTokens,
  getCurrentCompanyId,
  logout as logoutTokenManager,
} from "../../Utils/tokenManager";
import { currentAppUrl } from "../../Constants/config";
import {
  setUserProfilePic,
  setUserProfilePicDownloadUrl,
} from "../../Redux/store/userSlice";
import useAutoLogout from "../custom-logout/custom-logout";
import SessionWarningModal from "../custom-logout/session-timeout-popup";
import { clearPlatformNoticeSession } from "../../Utils/platformNoticeSession";
import { clearCardCurrencyName } from "../../Redux/store/userSlice";
import { clearAccountRestriction } from "../../Redux/store/accountRestrictionSlice";

function Header({ currentScreen, onBack, toggleMenu, isMenuOpen, isMobile }) {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const username = useSelector((state) => state.user.username);
  const [activeTab, setActiveTab] = useState(
    getActiveAccountType() === ACCOUNT_TYPES.PERSONAL ? "Personal" : "Company"
  );
  const [selectedItem, setSelectedItem] = useState(0);
  const [hoveredItem, setHoveredItem] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logoutDropdown = useRef(null);
  const profilePicURL = useSelector((state) => state.user.userProfilePic);
  const handleLogout = () => {
    clearPlatformNoticeSession();
    localStorage.removeItem("lastActivity");
    localStorage.removeItem("jwtToken");
    dispatch(clearCardCurrencyName());
    dispatch(clearAccountRestriction());
    window.location.href = "/login";
  };
  const { timeLeft, isWarning, stayConnected } = useAutoLogout(handleLogout, {
    inactivityLimit: 60 * 60 * 1000, // 10 minutes
    warningThreshold: 60 * 1000, // 60 seconds show warning
  });
  // Get companies from Redux state
  const { companies, loading } = useSelector((state) => state.company);

  // Get current user info from token manager
  const currentUserInfo = getCurrentUserInfo();
  // console.log(currentUserInfo)
  const currentCompanyId = getCurrentCompanyId();
  
  // Track account type to trigger re-fetch when it changes
  const [accountType, setAccountType] = useState(getActiveAccountType());

  // Determine display name based on account type
  const displayName =
    getActiveAccountType() === ACCOUNT_TYPES.PERSONAL
      ? currentUserInfo.fullName || username
      : currentUserInfo.name || "Company Account";

  // Account type label - show company name if in company mode
  const accountTypeLabel =
    getActiveAccountType() === ACCOUNT_TYPES.PERSONAL
      ? "Individual"
      : currentUserInfo.name || "Company";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        logoutDropdown.current &&
        !logoutDropdown.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch companies when activeTab is "Company" or on mount if in company mode
  useEffect(() => {
    const currentAccountType = getActiveAccountType();
    const shouldShowCompanyTab = currentAccountType === ACCOUNT_TYPES.COMPANY;
    
    // Update activeTab based on current account type
    setActiveTab(shouldShowCompanyTab ? "Company" : "Personal");
    setAccountType(currentAccountType);
    
    // Always fetch companies if in company mode (don't wait for empty check)
    if (shouldShowCompanyTab) {
      dispatch(getCompanies());
    }
  }, [dispatch]);

  // Ensure companies are loaded when Company tab is active
  useEffect(() => {
    if (activeTab === "Company") {
      const currentAccountType = getActiveAccountType();
      // Always ensure companies are fetched when Company tab is active
      if (currentAccountType === ACCOUNT_TYPES.COMPANY && companies.length === 0) {
        dispatch(getCompanies());
      }
    }
  }, [activeTab, dispatch, companies.length]);

  const handleChangeMenu = (id, action) => {
    setSelectedItem(id);

    if (toggleMenu) {
      toggleMenu();
    }

    if (action === "logout") {
      // Use token manager to logout
      sessionStorage.removeItem("redirectAfterLogin");
      dispatch(clearCardCurrencyName());
      dispatch(clearAccountRestriction());
      logoutTokenManager();
      navigate("/login");
    }
  };

  // Handle tab change between Personal and Company
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;

    setActiveTab(tab);

    if (tab === "Personal") {
      const success = switchToPersonal();
      if (success) {
        showToast("Switched to personal account", "success");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        showToast("Failed to switch to personal account", "error");
      }
    } else if (tab === "Company") {
      // Fetch companies when switching to Company tab
      if (companies.length === 0) {
        dispatch(getCompanies());
      }
      // Update account type state
      setAccountType(getActiveAccountType());
    }
  };

  // Handle click on a company item
  const handleCompanyClick = async (companyId, action, directorId) => {
    // Don't switch if clicking on the currently selected company
    const normalizedCompanyId = String(companyId);
    const normalizedCurrentId = currentCompanyId ? String(currentCompanyId) : null;
    
    if (normalizedCompanyId === normalizedCurrentId) {
      setIsDropdownOpen(false);
      // Just close dropdown, don't show toast for same company
      return;
    }

    // Close the dropdown
    setIsDropdownOpen(false);

    if (action === "company-select") {
      try {
        // Check if we already have tokens for this company
        if (hasCompanyTokens(companyId)) {
          // We already have tokens for this company, just switch
          const success = await switchToCompany(companyId);
          if (success) {
            showToast(`Switched to ${companies.find(c => String(c.id) === normalizedCompanyId)?.company_name || companies.find(c => String(c.id) === normalizedCompanyId)?.name || "company"}`, "success");
            localStorage.setItem('currentCompanyName', companies.find(c => String(c.id) === normalizedCompanyId)?.company_name || companies.find(c => String(c.id) === normalizedCompanyId)?.name || "company");
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            showToast(`Failed to switch to company account`, "error");
          }
        } else {
          // We need to fetch company tokens first
          const response = await dispatch(getCompanyDetails(companyId));
          
          if (response && response.payload) {
            // Extract company data from the response
            const companyData = response.payload;

            // Check if the API returns the correct data format
            if (companyData.access_token && companyData.refresh_token) {
              // Format timestamps if present
              if (companyData.created_at) {
                try {
                  // Standardize the timestamp format by removing microseconds
                  const timestamp =
                    companyData.created_at.split(".")[0] + "Z";
                  companyData.created_at = timestamp;
                } catch (error) {
                  console.error(
                    "Error formatting created_at timestamp:",
                    error
                  );
                }
              }

              // Save the company tokens
              saveCompanyTokens(
                companyId,
                companyData.access_token,
                companyData.refresh_token,
                {
                  id: companyId,
                  name: companyData.company_name || companyData.name || "Company",
                  directorId: companyData.director_id || directorId,
                  createdAt:
                    companyData.created_at ||
                    new Date().toISOString().split(".")[0] + "Z",
                }
              );

              // Switch to company account
              const success = await switchToCompany(companyId);
              if (success) {
                showToast(`Switched to ${companyData.company_name || companyData.name || "company"}`, "success");
                setTimeout(() => {
                  window.location.reload();
                }, 1000);
              } else {
                showToast(`Failed to switch to company account`, "error");
              }
            } else {
              showToast(`No valid tokens received for company`, "error");
            }
          } else {
            showToast(`Failed to get company details`, "error");
          }
        }
      } catch (error) {
        console.error("Error switching company:", error);
        showToast(`Error: ${error.message || "Failed to switch company"}`, "error");
      }
    }
  };

  const handleMenuToggle = () => {
    toggleMenu();
  };

  const toggleDropdown = (event) => {
    event.stopPropagation(); // Prevent click from propagating to the document listener
    
    // If opening dropdown and in company mode, ensure companies are loaded
    if (!isDropdownOpen) {
      const currentAccountType = getActiveAccountType();
      if (currentAccountType === ACCOUNT_TYPES.COMPANY) {
        // Set activeTab to Company if not already set
        setActiveTab("Company");
        setAccountType(currentAccountType);
        
        // Fetch companies if not already loaded
        if (companies.length === 0) {
          dispatch(getCompanies());
        }
      }
    }
    
    setIsDropdownOpen((prev) => !prev);
  };

  const menuItemspopUp = [
    {
      id: 1,
      action: "-",
      label: "Personal Account",
      defaultIcon: "/user-search.svg", // Replace with your default icon URL
      selectedIcon: "/user-search.svg", // Replace with your selected icon URL
    },
    {
      id: 3,
      action: "-",
      label: "Document References",
      defaultIcon: "/folder-2.svg",
      selectedIcon: "/folder-2-white.svg",
    },
    {
      id: 4,
      action: "logout",
      label: "Logout",
      defaultIcon: "/logout.svg",
      selectedIcon: "/logout-white.svg",
    },
  ];

  // Base company menu items
  const baseCompanyItems = [
    {
      id: 2,
      action: "-",
      label: "Manage Beneficiary",
      defaultIcon: "/people-black.svg",
      selectedIcon: "/people-white.svg",
    },
    {
      id: 3,
      action: "-",
      label: "Document References",
      defaultIcon: "/folder-2.svg",
      selectedIcon: "/folder-2-white.svg",
    },
    {
      id: 4,
      label: "Logout",
      action: "logout",
      defaultIcon: "/logout.svg",
      selectedIcon: "/logout-white.svg",
    },
  ];

  // Create company menu items based on fetched companies
  // Ensure current company is included even if not in the list
  const menuItemspopUpCompany = (() => {
    // Normalize currentCompanyId to string for comparison
    const normalizedCurrentCompanyId = currentCompanyId ? String(currentCompanyId) : null;
    
    const companyItems = companies.length > 0
      ? companies.map((company) => {
          const companyIdStr = String(company.id);
          return {
            id: `company-${company.id}`,
            label: company.company_name || company.name || "Company",
            defaultIcon: "/buliding.svg",
            selectedIcon: "/buliding-white.svg",
            isCompany: true,
            action: "company-select",
            companyId: company.id,
            directorId: company.director_id,
            isSelected: normalizedCurrentCompanyId === companyIdStr,
          };
        })
      : [];

    // If we're in company mode but current company is not in the list, add it
    if (normalizedCurrentCompanyId && accountType === ACCOUNT_TYPES.COMPANY) {
      const currentCompanyInList = companyItems.some(
        (item) => String(item.companyId) === normalizedCurrentCompanyId
      );
      
      if (!currentCompanyInList && currentUserInfo && currentUserInfo.name) {
        // Add current company to the list at the top
        companyItems.unshift({
          id: `company-${normalizedCurrentCompanyId}`,
          label: currentUserInfo.name || "Current Company",
          defaultIcon: "/buliding.svg",
          selectedIcon: "/buliding-white.svg",
          isCompany: true,
          action: "company-select",
          companyId: normalizedCurrentCompanyId,
          directorId: currentUserInfo.directorId,
          isSelected: true,
        });
      }
    }

    // Sort companies: selected company first, then others
    const sortedCompanyItems = [...companyItems].sort((a, b) => {
      if (a.isSelected) return -1;
      if (b.isSelected) return 1;
      return 0;
    });

    return [
      ...sortedCompanyItems,
      // Add the standard menu items
      ...baseCompanyItems,
    ];
  })();
  const handleNotifications = () => {
    const token = localStorage.getItem("jwtToken");
    if (!token) {
      localStorage.removeItem("jwtToken");
      navigate("/login");
      showToast("Session expired. Please log in again.", "error");
    }
    window.location.href = `${currentAppUrl}/notifications?bTok=${token}`;
  };
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem("jwtToken");
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          if (!window.location.pathname.startsWith("/login")) {
            sessionStorage.setItem(
              "redirectAfterLogin",
              JSON.stringify({
                path: window.location.pathname + window.location.search,
                state: {},
              })
            );
          }
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }
      return await response.json();
    } catch (error) {
      console.error("API Call Error:", error); // Log full error for debugging
      throw new Error(
        typeof error.message === "string"
          ? error.message
          : "Network error, please try again later"
      );
    }
  };
  const getProfilePicture = async () => {
    try {
      const response = await GetAPIcalls("/v1/user/me");

      if (response.status == 200) {
        if (response.data.profile_image_url != null) {
          dispatch(setUserProfilePic(response.data.profile_image_url));
          dispatch(
            setUserProfilePicDownloadUrl(
              response.data.profile_image_url_download
            )
          );
        }
      } else {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
        }
      }
    } catch (err) {
      showToast(err.message, "error");
    } finally {
    }
  };
  useEffect(() => {
    getProfilePicture();
  }, []);
  return (
    <div className="flex justify-between mb-4 items-center px-4 py-2 h-16">
      {/* Back Button */}
      {!isMobile && (
        <div className="flex-shrink-0 mt-4">
          {currentScreen !== "/my-investments" &&
            currentScreen !== "/my-profile" &&
            currentScreen !== "/reports" &&
            currentScreen !== "/inspecie-transfer" &&
            currentScreen !== "/" &&
            currentScreen !== "/investment-hub" && (
              <button
                className="flex items-center space-x-2 bg-white px-3 py-3 rounded-xl shadow-md"
                onClick={onBack}
              >
                <img src="/arrow-left.svg" alt="Back" className="w-4 h-4" />
                <span className="text-sm lg:inline hidden font-medium">
                  Back
                </span>
              </button>
            )}
        </div>
      )}

      {/* Menu Icon for Mobile */}
      <div className="flex items-center mt-4 bg-white rounded-xl space-x-6">
        {isMobile && (
          <div
            className="cursor-pointer p-2 relative z-20"
            onClick={handleMenuToggle}
          >
            <div
              className={`w-6 h-0.5 bg-black my-1 transition-transform duration-300 ${
                isMenuOpen ? "rotate-45 translate-y-2" : ""
              }`}
            />
            <div
              className={`w-6 h-0.5 bg-black my-1 transition-opacity duration-300 ${
                isMenuOpen ? "opacity-0" : "opacity-100"
              }`}
            />
            <div
              className={`w-6 h-0.5 bg-black my-1 transition-transform duration-300 ${
                isMenuOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            />
          </div>
        )}
      </div>
      <div className="flex items-center space-x-4 ml-auto">
        {/* Session Timer - Only show last 2 mins */}

        {/* Profile and Notifications */}
        <div
          ref={logoutDropdown}
          className="relative mt-4 md:mr-2  flex items-center space-x-6"
        >
          {/* Profile Dropdown */}
          <div
            onClick={toggleDropdown}
            className={`flex items-center space-x-2 rounded-xl p-2 cursor-pointer transition-colors duration-300 ${
              isDropdownOpen ? "bg-black text-white" : "bg-white text-black"
            }`}
          >
            <img
              src={profilePicURL}
              alt="User Avatar"
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
            />
            <div className="hidden sm:block text-sm whitespace-nowrap">
              <p className={`font-medium ${isDropdownOpen ? "text-white" : "text-[#005A9C]"}`}>
                {getActiveAccountType() === ACCOUNT_TYPES.PERSONAL ? accountTypeLabel : 'Corporate'}
              </p>
              <p className={`text-xs ${isDropdownOpen ? "text-gray-300" : "text-gray-600"}`}>
                {getActiveAccountType() === ACCOUNT_TYPES.PERSONAL ? displayName : localStorage.getItem('currentCompanyName')}
              </p>
            </div>
            <button className="focus:outline-none">
              <img
                src={!isDropdownOpen ? "/header-down.svg" : "/chevron-down.svg"}
                alt="Dropdown Icon"
                className={`transition-all duration-300 ease-in-out transform ${
                  isDropdownOpen ? "scale-100" : "scale-100"
                }`}
              />
            </button>
          </div>
          {isDropdownOpen && (
            <div
              className="absolute top-16 right-0 w-48 md:right-20 sm:w-56 bg-white rounded-lg shadow-lg z-[20]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex border-b">
                <button
                  className={`w-1/2 py-2 rounded-tl-lg text-sm font-medium transition-colors duration-300 ${
                    activeTab === "Personal"
                      ? "bg-black text-white"
                      : "bg-white text-black"
                  }`}
                  onClick={() => handleTabChange("Personal")}
                >
                  Personal
                </button>
                <button
                  className={`w-1/2 py-2 rounded-tr-lg text-sm font-medium transition-colors duration-300 ${
                    activeTab === "Company"
                      ? "bg-black text-white"
                      : "bg-white text-black"
                  }`}
                  onClick={() => handleTabChange("Company")}
                >
                  Company
                </button>
              </div>
              <ul className="py-2 px-2 max-h-80 overflow-y-auto">
                {loading && (
                  <li className="flex justify-center py-2">
                    <span className="text-sm text-gray-500">Loading...</span>
                  </li>
                )}
                {!loading &&
                  (activeTab === "Personal"
                    ? menuItemspopUp
                    : menuItemspopUpCompany
                  ).map((item, index) => {
                    const menuItems = activeTab === "Personal" ? menuItemspopUp : menuItemspopUpCompany;
                    const prevItem = index > 0 ? menuItems[index - 1] : null;
                    const showSeparator = activeTab === "Company" && 
                                         !item.isCompany && 
                                         prevItem?.isCompany;
                    
                    return (
                      <React.Fragment key={item.id}>
                        {/* Add separator before menu items (after companies) */}
                        {showSeparator && (
                          <li className="border-t border-gray-200 my-1" key={`separator-${item.id}`}></li>
                        )}
                    <li
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      onClick={() =>
                        item.isCompany
                          ? handleCompanyClick(
                              item.companyId,
                              item.action,
                              item.directorId
                            )
                          : handleChangeMenu(item.id, item.action)
                      }
                      className={`flex items-center px-4 py-2 rounded-md space-x-2 cursor-pointer transition-all duration-300 ${
                        hoveredItem === item.id || selectedItem === item.id || (item.isCompany && item.isSelected)
                          ? "bg-black text-white"
                          : "text-black hover:bg-black hover:text-white"
                      }`}
                    >
                      <img
                        src={
                          hoveredItem === item.id || selectedItem === item.id || (item.isCompany && item.isSelected)
                            ? item.selectedIcon
                            : item.defaultIcon
                        }
                        alt={item.label}
                        className="w-4 h-4 sm:w-5 sm:h-5"
                      />
                      <span className={`text-xs sm:text-sm flex-1 ${item.isCompany && item.isSelected ? 'font-semibold' : ''}`}>
                        {item.label}
                      </span>
                      {item.isCompany && item.isSelected && (
                        <span className="text-xs font-bold">✓</span>
                      )}
                    </li>
                    </React.Fragment>
                  );
                })}
              </ul>
            </div>
          )}
          {/* Notifications */}
          {/* <button
          onClick={handleNotifications}
          aria-label="Notifications"
          className="relative hover:text-gray-300 h-12 rounded-xl bg-white focus:outline-none"
        >
          <img
            src="/lamp.svg"
            alt="Notifications"
            className="w-6 m-2 h-6 sm:w-10"
          />
          <span className="absolute top-0 -right-2 bg-red-500 text-xs text-white rounded-full w-5 h-5 flex items-center justify-center">
            3
          </span>
        </button> */}
        </div>
      </div>
      <SessionWarningModal
        isOpen={isWarning}
        timeLeft={timeLeft}
        onStayConnected={stayConnected}
        onLogout={handleLogout}
      />
    </div>
  );
}

export default Header;
